import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import ConfirmModal from "../components/ConfirmModal";

const ETATS = {
  neuf:   { label: "Neuf",     color: "#27ae60" },
  bon:    { label: "Bon",      color: "#1d4f91" },
  moyen:  { label: "Moyen",    color: "#f39c12" },
  abime:  { label: "Abîmé",    color: "#e74c3c" },
};
const STATUTS = {
  disponible:    { label: "Disponible",     color: "#27ae60" },
  reserve:       { label: "Réservé",        color: "#f39c12" },
  emprunte:      { label: "Emprunté",       color: "#e74c3c" },
  indisponible:  { label: "Indisponible",   color: "#888"    },
};

const API_BASE = process.env.REACT_APP_API_BASE || "/api";

function fmtFR(iso) {
  if (!iso) return "";
  return new Date(iso + (iso.includes("T") ? "" : "T00:00:00"))
    .toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

export default function BibliothequeLivreDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [livre, setLivre] = useState(null);
  const [error, setError] = useState("");
  const [activePhoto, setActivePhoto] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [reserving, setReserving] = useState(false);
  const [reserveError, setReserveError] = useState("");
  const [reserveSuccess, setReserveSuccess] = useState(false);

  useEffect(() => {
    api.get(`/bibliotheque/livres/${id}`)
      .then(setLivre)
      .catch(() => setError("Livre introuvable."));
  }, [id]);

  const handleReserver = async () => {
    setReserving(true);
    setReserveError("");
    try {
      await api.post("/bibliotheque/reservations", {
        livre_id: parseInt(id, 10),
        message: message,
      });
      setReserveSuccess(true);
      setShowConfirm(false);
      // Recharger le livre (statut maintenant à "reserve")
      const updated = await api.get(`/bibliotheque/livres/${id}`);
      setLivre(updated);
    } catch (e) {
      setReserveError(e.message || "Erreur lors de la réservation");
    } finally {
      setReserving(false);
    }
  };

  if (error) return <div className="container"><div className="error">{error}</div></div>;
  if (!livre) return <div className="container"><p className="muted">Chargement…</p></div>;

  const statut = STATUTS[livre.statut] || STATUTS.indisponible;
  const etat = ETATS[livre.etat] || ETATS.bon;
  const peutReserver = livre.statut === "disponible" && user;
  const photos = livre.photos || [];
  const photo = photos[activePhoto] || null;

  return (
    <div className="container">
      <Link to="/bibliotheque" className="muted" style={{ textDecoration: "none" }}>
        ← Retour au catalogue
      </Link>

      <div className="biblio-detail">
        {/* Galerie photos */}
        <div className="biblio-detail-gallery">
          <div className="biblio-detail-main-photo">
            {photo ? (
              <img src={`${API_BASE}${photo.url}`} alt={livre.titre} />
            ) : (
              <div className="biblio-detail-no-photo">📕</div>
            )}
            <div className="biblio-detail-statut" style={{ background: statut.color }}>
              {statut.label}
            </div>
          </div>
          {photos.length > 1 && (
            <div className="biblio-detail-thumbs">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActivePhoto(i)}
                  className={`biblio-detail-thumb ${i === activePhoto ? "active" : ""}`}>
                  <img src={`${API_BASE}${p.url}`} alt="" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Infos + actions */}
        <div className="biblio-detail-info">
          <h1 style={{ marginTop: 0 }}>{livre.titre}</h1>
          {livre.auteur && (
            <p style={{ fontSize: "1.1em", margin: "0 0 1rem" }}>
              <strong>{livre.auteur}</strong>
            </p>
          )}

          <table className="biblio-detail-table">
            <tbody>
              {livre.editeur && (
                <tr><td>Éditeur</td><td>{livre.editeur}</td></tr>
              )}
              {livre.annee && (
                <tr><td>Année</td><td>{livre.annee}</td></tr>
              )}
              {livre.isbn && (
                <tr><td>ISBN</td><td className="biblio-isbn">{livre.isbn}</td></tr>
              )}
              {livre.genre && (
                <tr><td>Genre</td><td>{livre.genre}</td></tr>
              )}
              <tr>
                <td>État</td>
                <td><span style={{ color: etat.color, fontWeight: 500 }}>● {etat.label}</span></td>
              </tr>
              {livre.emplacement && (
                <tr><td>Emplacement</td><td>{livre.emplacement}</td></tr>
              )}
              {livre.statut === "emprunte" && livre.date_retour_prevue && (
                <tr>
                  <td>Retour prévu</td>
                  <td><strong>{fmtFR(livre.date_retour_prevue)}</strong></td>
                </tr>
              )}
            </tbody>
          </table>

          {livre.resume && (
            <div style={{ marginTop: "1.5rem" }}>
              <h3>Résumé</h3>
              <div className="biblio-resume">
                {livre.resume.split("\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          )}

          {/* Bouton de réservation */}
          <div className="biblio-detail-actions">
            {reserveSuccess && (
              <div className="success" style={{ marginBottom: "1rem" }}>
                ✓ Votre demande de réservation a bien été enregistrée. Le libraire la
                validera prochainement et vous serez prévenu par email.{" "}
                <Link to="/mes-reservations">Voir mes réservations</Link>
              </div>
            )}
            {reserveError && (
              <div className="error" style={{ marginBottom: "1rem" }}>
                {reserveError}
              </div>
            )}

            {peutReserver && !reserveSuccess && (
              <>
                <button className="btn-primary"
                        onClick={() => setShowConfirm(true)}
                        style={{ fontSize: "1.05rem", padding: "0.8rem 1.6rem" }}>
                  📚 Réserver ce livre
                </button>
                <p className="muted" style={{ fontSize: "0.85em", marginTop: "0.6rem" }}>
                  Votre demande sera étudiée par le libraire. Vous recevrez un email
                  lorsqu'elle sera validée.
                </p>
              </>
            )}

            {livre.statut === "disponible" && !user && (
              <div className="card" style={{ background: "rgba(29,79,145,0.06)", padding: "1rem" }}>
                <p style={{ margin: 0 }}>
                  Pour réserver ce livre, vous devez{" "}
                  <Link to="/connexion">vous connecter</Link>{" "}
                  ou <Link to="/inscription">créer un compte</Link>.
                </p>
              </div>
            )}

            {livre.statut !== "disponible" && (
              <div className="card" style={{ background: "#fdf6e3", padding: "1rem" }}>
                <p style={{ margin: 0 }}>
                  Ce livre n'est actuellement pas disponible à l'emprunt.
                  {livre.statut === "emprunte" && livre.date_retour_prevue && (
                    <> Retour prévu le <strong>{fmtFR(livre.date_retour_prevue)}</strong>.</>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showConfirm && (
        <div className="modal-overlay" onClick={() => !reserving && setShowConfirm(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}
               style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Réserver « {livre.titre} »</h3>
              <button className="modal-close" onClick={() => !reserving && setShowConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              {reserveError && <div className="error">{reserveError}</div>}
              <p>
                Vous êtes sur le point de demander la réservation de ce livre.
                Le libraire validera votre demande et vous serez prévenu par email
                lorsque vous pourrez venir le chercher aux horaires d'ouverture.
              </p>
              <label style={{ display: "block", marginTop: "1rem" }}>
                Message au libraire (facultatif) :
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows="3"
                  placeholder="Ex: J'aimerais venir le chercher samedi matin"
                  style={{ width: "100%", marginTop: "0.4rem",
                           padding: "0.5rem", border: "1px solid rgba(0,0,0,0.18)",
                           borderRadius: 5, fontFamily: "inherit" }}
                />
              </label>
            </div>
            <div className="modal-footer">
              <button className="secondary" onClick={() => setShowConfirm(false)}
                      disabled={reserving}>Annuler</button>
              <button className="btn-primary" onClick={handleReserver}
                      disabled={reserving}>
                {reserving ? "Envoi..." : "Confirmer la réservation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
